/**
 * Loads the signed-in user. Everything in the frame — the nav, the scope
 * selector, which columns render — comes from this one response, because it
 * carries the permission matrix the API enforces with (plan §8).
 */
import { useEffect, useState } from 'react'
import { ApiError, type MeResponse } from '@rowted/shared'
import { request, setCsrfToken } from './api.js'

export type MeState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'error'; message: string }
  | { status: 'ready'; me: MeResponse }

export function useMe(): MeState {
  const [state, setState] = useState<MeState>({ status: 'loading' })

  useEffect(() => {
    let cancelled = false

    request<MeResponse>('/me')
      .then((me) => {
        if (cancelled) return
        // Sign-in is not the only way into the app: a reload, a bookmark or a
        // link arrives with a valid cookie and no token, and every mutation —
        // list reads included — would be refused without this.
        setCsrfToken(me.csrfToken)
        setState({ status: 'ready', me })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        if (error instanceof ApiError
            && ['unauthenticated', 'session_expired'].includes(error.code)) {
          setState({ status: 'signed-out' })
          return
        }
        setState({
          status: 'error',
          message: error instanceof ApiError ? error.message : 'Could not reach the server.',
        })
      })

    return () => { cancelled = true }
  }, [])

  return state
}
